import {
  Box,
  Container,
  Heading,
  Text,
  SimpleGrid,
  VStack,
  useColorModeValue,
} from "@chakra-ui/react";
import { motion, useInView } from "framer-motion";
import { useRef, useState, useEffect } from "react";

const MotionBox = motion(Box);
const MotionText = motion(Text);

// Counter animation hook
const useCountUp = (
  end: number,
  duration: number = 2000,
  delay: number = 0,
  shouldStart: boolean = false,
) => {
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!shouldStart) return;

    const timeout = setTimeout(() => {
      let startTime: number | null = null;
      const step = (timestamp: number) => {
        if (!startTime) startTime = timestamp;
        const progress = Math.min((timestamp - startTime) / duration, 1);

        // Easing function for smooth animation
        const easeOut = 1 - Math.pow(1 - progress, 3);
        setCount(Math.floor(easeOut * end));

        if (progress < 1) {
          requestAnimationFrame(step);
        }
      };
      requestAnimationFrame(step);
    }, delay);

    return () => clearTimeout(timeout);
  }, [end, duration, delay, shouldStart]);

  return count;
};

export const StatsSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.3 });

  const textColor = useColorModeValue("gray.800", "white");
  const subtitleColor = useColorModeValue("gray.600", "gray.400");
  const statNumberGradient = useColorModeValue(
    "linear(to-br, brand.500, brand.600)",
    "linear(to-br, brand.400, brand.500)",
  );
  const statNumberShadow = useColorModeValue(
    "0 0 40px rgba(173, 11, 6, 0.15)",
    "0 0 40px rgba(173, 11, 6, 0.3)",
  );

  // Stats counting animation - starts when section is in view
  const investorCount = useCountUp(110000, 2500, 100, isInView);
  const fundCount = useCountUp(7, 2000, 300, isInView);
  const returnRate = useCountUp(80, 2200, 500, isInView);

  const stats = [
    {
      value: investorCount,
      suffix: "+",
      label: "سرمایه‌گذار فعال",
      delay: 0.1,
    },
    {
      value: fundCount,
      suffix: "+",
      label: "صندوق سرمایه‌گذاری",
      delay: 0.3,
    },
    {
      value: returnRate,
      suffix: "+",
      label: "همت دارایی",
      delay: 0.5,
    },
  ];

  return (
    <Box id="stats" py={20} ref={ref}>
      <Container maxW="container.xl">
        <VStack spacing={16}>
          {/* Section Header */}
          <VStack spacing={4} textAlign="center">
            <Heading
              as="h2"
              fontSize={{ base: "xl", md: "2xl", lg: "3xl" }}
              color={textColor}
            >
              آمار و اعداد تأمین سرمایه امید
            </Heading>
            <Text
              fontSize={{ base: "md", md: "lg" }}
              color={subtitleColor}
              maxW="600px"
            >
              اعتماد هزاران سرمایه‌گذار، نتیجه عملکرد شفاف و حرفه‌ای ماست
            </Text>
          </VStack>

          {/* Stats Grid */}
          <SimpleGrid
            columns={{ base: 1, md: 3 }}
            spacing={{ base: 12, md: 16 }}
            w="100%"
          >
            {stats.map((stat, index) => (
              <MotionBox
                key={stat.label}
                initial={{ opacity: 0, y: 30 }}
                animate={
                  isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }
                }
                transition={{ duration: 0.6, delay: stat.delay }}
              >
                <VStack spacing={4}>
                  <MotionText
                    fontSize={{ base: "5xl", md: "6xl" }}
                    fontWeight="extrabold"
                    bgGradient={statNumberGradient}
                    bgClip="text"
                    textShadow={statNumberShadow}
                    initial={{ scale: 0.5 }}
                    animate={isInView ? { scale: 1 } : { scale: 0.5 }}
                    transition={{ duration: 0.5, delay: stat.delay + 0.2 }}
                  >
                    {stat.suffix === "+" ? "+" : ""}
                    {stat.value.toLocaleString("fa-IR")}
                  </MotionText>
                  <Text
                    fontSize={{ base: "lg", md: "xl" }}
                    color={subtitleColor}
                    fontWeight="medium"
                    textAlign="center"
                  >
                    {stat.label}
                  </Text>
                </VStack>
              </MotionBox>
            ))}
          </SimpleGrid>
        </VStack>
      </Container>
    </Box>
  );
};
